import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { BreathingAura } from '../components/BreathingAura';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const Chat = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadHistory = async () => {
    try {
      const data = await api.getChatHistory();
      setMessages(data);
    } catch (error) {
      toast.error('Failed to load chat history');
    } finally {
      setInitialLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        message: userMessage,
        is_user: true,
        timestamp: new Date().toISOString(),
      },
    ]);

    setLoading(true);
    try {
      const aiMessage = await api.sendChatMessage(userMessage);
      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-primary text-xl font-serif">Loading chat...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative flex flex-col">
      <BreathingAura variant="calm" />
      
      <div className="relative z-10 flex flex-col flex-1">
        <div className="container mx-auto px-6 py-8 max-w-4xl flex-1 flex flex-col">
          <Button
            data-testid="back-to-dashboard-from-chat"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="rounded-full mb-8 self-start"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex-1 flex flex-col"
          >
            <div className="text-center mb-8">
              <h1 className="text-5xl md:text-6xl font-serif font-light text-foreground tracking-tight mb-2">
                Chat with AURA
              </h1>
              <p className="text-muted-foreground text-lg">Your compassionate AI companion</p>
            </div>

            <div className="flex-1 bg-card/50 backdrop-blur-xl border border-white/5 rounded-3xl p-8 mb-6 overflow-hidden flex flex-col">
              <div className="flex-1 overflow-y-auto space-y-6 pr-4" style={{ maxHeight: '60vh' }}>
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-center text-muted-foreground">
                    <div>
                      <p className="text-lg mb-2">Welcome! I'm AURA.</p>
                      <p>How can I support you today?</p>
                    </div>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      data-testid={msg.is_user ? 'user-message' : 'ai-message'}
                      className={`flex ${
                        msg.is_user ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl p-4 ${
                          msg.is_user
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted/50 text-foreground'
                        }`}
                      >
                        <p className="leading-relaxed whitespace-pre-wrap">{msg.message}</p>
                        <div
                          className={`text-xs mt-2 ${
                            msg.is_user ? 'text-primary-foreground/70' : 'text-muted-foreground'
                          }`}
                        >
                          {new Date(msg.timestamp).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
                {loading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className="bg-muted/50 text-foreground rounded-2xl p-4">
                      <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                  </motion.div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            <form onSubmit={handleSubmit} className="flex gap-3">
              <Input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                disabled={loading}
                className="flex-1 bg-input/50 border-transparent focus:border-primary/50 focus:ring-2 focus:ring-primary/20 rounded-2xl h-14 px-6 text-lg placeholder:text-muted-foreground/50"
              />
              <Button
                data-testid="send-message-button"
                type="submit"
                disabled={loading || !input.trim()}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full w-14 h-14 transition-all hover:scale-105 active:scale-95 shadow-[0_0_20px_-5px_rgba(141,163,153,0.5)] disabled:opacity-50"
              >
                <Send className="w-5 h-5" />
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Chat;